<template>
  <div class="max-w-[1400px] mx-auto">
    <!-- Main page title -->
    <h1 class="mb-6 text-[#333] text-[28px] font-semibold">
      Pedidos de Reserva
    </h1>

    <!-- Dashboard Stats Section -->
    <div class="mb-2">
      <div
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-5"
      >
        <!-- Open Reservations Card -->
        <div
          @click="cardFilter('Open')"
          class="flex items-center gap-4 p-6 rounded-xl bg-white shadow-md border-l-4 border-[#1976d2] hover:shadow-lg hover:-translate-y-0.5 transition"
        >
          <div
            class="w-[50px] h-[50px] flex items-center justify-center rounded-xl bg-[rgba(25,118,210,0.1)] text-[#1976d2]"
          >
            <i class="fas fa-calendar-plus text-xl"></i>
          </div>
          <div class="flex-1">
            <div class="text-[28px] font-bold leading-none text-[#1976d2] mb-1">
              {{ dashboard?.numberOfOpenReservations }}
            </div>
            <div
              class="text-sm font-medium uppercase tracking-wide text-[#1976d2] opacity-80"
            >
              Abertas
            </div>
          </div>
        </div>

        <!-- Confirmed Reservations Card -->
        <div
          @click="cardFilter('Confirmed')"
          class="flex items-center gap-4 p-6 rounded-xl bg-white shadow-md border-l-4 border-[#388e3c] hover:shadow-lg hover:-translate-y-0.5 transition"
        >
          <div
            class="w-[50px] h-[50px] flex items-center justify-center rounded-xl bg-[rgba(56,142,60,0.1)] text-[#388e3c]"
          >
            <i class="fas fa-calendar-check text-xl"></i>
          </div>
          <div class="flex-1">
            <div class="text-[28px] font-bold leading-none text-[#388e3c] mb-1">
              {{ dashboard?.numberOfConfirmedReservations }}
            </div>
            <div
              class="text-sm font-medium uppercase tracking-wide text-[#388e3c] opacity-80"
            >
              Confirmadas
            </div>
          </div>
        </div>

        <!-- Closed Reservations Card -->
        <div
          @click="cardFilter('Closed')"
          class="flex items-center gap-4 p-6 rounded-xl bg-white shadow-md border-l-4 border-[#7b1fa2] hover:shadow-lg hover:-translate-y-0.5 transition"
        >
          <div
            class="w-[50px] h-[50px] flex items-center justify-center rounded-xl bg-[rgba(123,31,162,0.1)] text-[#7b1fa2]"
          >
            <i class="fas fa-calendar-times text-xl"></i>
          </div>
          <div class="flex-1">
            <div class="text-[28px] font-bold leading-none text-[#7b1fa2] mb-1">
              {{ dashboard?.numberOfClosedReservations }}
            </div>
            <div
              class="text-sm font-medium uppercase tracking-wide text-[#7b1fa2] opacity-80"
            >
              Fechadas
            </div>
          </div>
        </div>

        <!-- Canceled Reservations Card -->
        <div
          @click="cardFilter('Canceled')"
          class="flex items-center gap-4 p-6 rounded-xl bg-white shadow-md border-l-4 border-[#d32f2f] hover:shadow-lg hover:-translate-y-0.5 transition"
        >
          <div
            class="w-[50px] h-[50px] flex items-center justify-center rounded-xl bg-[rgba(211,47,47,0.1)] text-[#d32f2f]"
          >
            <i class="fas fa-calendar-minus text-xl"></i>
          </div>
          <div class="flex-1">
            <div class="text-[28px] font-bold leading-none text-[#d32f2f] mb-1">
              {{ dashboard?.numberOfCanceledReservations }}
            </div>
            <div
              class="text-sm font-medium uppercase tracking-wide text-[#d32f2f] opacity-80"
            >
              Canceladas
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Menu Section -->
    <div class="filter-menu w-full bg-white p-6 mb-5 rounded-lg shadow-md">
      <h1 class="text-[#333] text-[22px] font-semibold mb-5">Filtros</h1>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
        <!-- Funcionário -->
        <div class="flex flex-col gap-1">
          <label for="employee" class="font-semibold text-gray-800 text-sm"
            >Funcionário</label
          >
          <input
            id="employee"
            type="text"
            maxlength="50"
            placeholder="Ex: João Silva"
            v-model="appliedFilters.employee"
            class="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#28b473] focus:border-[#28b473]"
          />
        </div>

        <!-- Marca -->
        <div class="flex flex-col gap-1">
          <label for="vehicleBrand" class="font-semibold text-gray-800 text-sm"
            >Marca</label
          >
          <input
            id="vehicleBrand"
            type="text"
            maxlength="30"
            placeholder="Ex: Toyota"
            v-model="appliedFilters.vehicleBrand"
            class="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#28b473] focus:border-[#28b473]"
          />
        </div>

        <!-- Matrícula -->
        <div class="flex flex-col gap-1">
          <label
            for="vehicleLicensePlate"
            class="font-semibold text-gray-800 text-sm"
            >Matrícula</label
          >
          <input
            id="vehicleLicensePlate"
            type="text"
            maxlength="8"
            placeholder="Ex: 12-AB-34"
            v-model="appliedFilters.vehicleLicensePlate"
            class="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#28b473] focus:border-[#28b473]"
          />
        </div>

        <!-- ID Reserva -->
        <div class="flex flex-col gap-1">
          <label for="reservationId" class="font-semibold text-gray-800 text-sm"
            >Id Reserva</label
          >
          <input
            id="reservationId"
            type="text"
            maxlength="8"
            placeholder="Ex: 700000"
            v-model="appliedFilters.reservationId"
            class="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#28b473] focus:border-[#28b473]"
          />
        </div>
      </div>

      <!-- Linha seguinte (datas e estado) -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
        <!-- Data de Início -->
        <div class="flex flex-col gap-1">
          <label for="startDate" class="font-semibold text-gray-800 text-sm"
            >Data de Início de Reserva</label
          >
          <Datepicker
            id="startDate"
            v-model="appliedFilters.startDate"
            :format-locale="pt"
            :format="formatDateTime"
            locale="pt-PT"
            :clearable="true"
            placeholder="Selecionar data"
            :enable-time-picker="true"
            select-text="Selecionar"
            cancel-text="Cancelar"
            now-button-label="Agora"
            input-class="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#28b473] focus:border-[#28b473]"
          />
        </div>

        <!-- Data de Fim -->
        <div class="flex flex-col gap-1">
          <label for="endDate" class="font-semibold text-gray-800 text-sm"
            >Data de Fim de Reserva</label
          >
          <Datepicker
            id="endDate"
            v-model="appliedFilters.endDate"
            :format-locale="pt"
            :format="formatDateTime"
            locale="pt-PT"
            :clearable="true"
            placeholder="Selecionar data"
            :enable-time-picker="true"
            select-text="Selecionar"
            cancel-text="Cancelar"
            now-button-label="Agora"
            input-class="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#28b473] focus:border-[#28b473]"
          />
        </div>

        <!-- Estado -->
        <div class="flex flex-col gap-1 col-span-1 md:col-span-1 lg:col-span-2">
          <label class="font-semibold text-gray-800 text-sm">Estado</label>
          <div class="flex flex-wrap gap-x-4 gap-y-2">
            <label
              v-for="option in statusOptions"
              :key="option.value"
              class="flex items-center gap-2 cursor-pointer select-none text-gray-700 text-sm"
            >
              <input
                type="checkbox"
                :value="option.value"
                v-model="appliedFilters.status"
                class="cursor-pointer"
              />
              <span
                class="w-2.5 h-2.5 rounded-full inline-block"
                :class="{
                  'bg-[#1976d2]': option.value === 'Open',
                  'bg-[#388e3c]': option.value === 'Confirmed',
                  'bg-[#d32f2f]': option.value === 'Canceled',
                  'bg-[#7b1fa2]': option.value === 'Closed',
                }"
              ></span>
              {{ option.label }}
            </label>
          </div>
        </div>
      </div>

      <!-- Filter Action Buttons -->
      <div
        class="filter-row flex flex-wrap gap-6 justify-items-start justify-end"
      >
        <div class="filter-actions flex gap-4 items-end min-w-[200px]">
          <!-- Clear Filters Button -->
          <button
            class="bg-gray-100 text-gray-700 border border-gray-300 py-2 px-6 rounded-md font-semibold hover:bg-gray-200 transition-transform transform hover:-translate-y-0.5 cursor-pointer"
            :class="{
              'bg-red-50 border-red-300 text-red-700': hasActiveFilters,
            }"
            @click="clearFilters"
          >
            Limpar filtros
            <!-- Show count of active filters -->
            <span v-if="hasActiveFilters" class="ml-1">
              ({{ getActiveFiltersCount() }})
            </span>
          </button>

          <!-- Apply Filters Button -->
          <button
            data-testid="filter-button"
            class="min-w-[160px] bg-primary text-white py-2 px-6 rounded-md font-semibold hover:bg-green-600 transition-transform transform hover:-translate-y-0.5 cursor-pointer relative"
            @click="applyFilters"
          >
            Filtrar
            <!-- Notification badge for unapplied changes -->
            <span
              v-if="hasUnappliedFilters"
              class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center"
            >
              !
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Applied Filters Status Bar -->
    <div
      v-if="hasActiveFilters"
      class="applied-filters-status mt-2 mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md"
    >
      <div class="flex items-center gap-2 mb-2">
        <!-- Filter icon -->
        <svg
          class="h-4 w-4 text-blue-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
          />
        </svg>
        <span class="text-sm font-medium text-blue-800"
          >Filtros aplicados:</span
        >
      </div>

      <!-- Display active filter tags -->
      <div class="flex flex-wrap gap-2">
        <span v-if="store.filters.employee" class="filter-tag">
          Funcionário: {{ store.filters.employee }}
        </span>
        <span v-if="store.filters.vehicleBrand" class="filter-tag">
          Marca: {{ store.filters.vehicleBrand }}
        </span>
        <span v-if="store.filters.vehicleLicensePlate" class="filter-tag">
          Matrícula: {{ store.filters.vehicleLicensePlate }}
        </span>
        <span v-if="store.filters.reservationId" class="filter-tag">
          Id Reserva: {{ store.filters.reservationId }}
        </span>
        <span v-if="store.filters.startDate" class="filter-tag">
          Início: {{ formatDate(store.filters.startDate) }}
        </span>
        <span v-if="store.filters.endDate" class="filter-tag">
          Fim: {{ formatDate(store.filters.endDate) }}
        </span>
        <span
          v-if="store.filters.status && store.filters.status.length > 0"
          class="filter-tag"
        >
          Estado:
          {{
            store.filters.status
              .map((value) => $t(`common.scheduledReservationStatus.${value}`))
              .join(", ")
          }}
        </span>
      </div>
    </div>

    <!-- Main Table Container -->
    <div
      class="bg-white shadow-[0_2px_10px_rgba(0,0,0,0.3)] mt-2 relative overflow-visible z-[1] rounded-lg"
    >
      <!-- Table Controls: Entries per page selector -->
      <div
        class="table-controls px-6 py-4 border-b border-gray-200 flex justify-between items-center"
      >
        <div class="entries-selector">
          <label for="entriesPerPage">Mostrar:</label>
          <select
            id="entriesPerPage"
            v-model="store.filters.pageSize"
            @change="onEntriesPerPageChange"
            :disabled="isLoading"
            class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option
              v-for="option in entriesPerPageOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <span>entradas</span>
        </div>
        <button
          class="bg-[#ffa000] text-white py-2 px-6 rounded-md font-semibold hover:bg-[#ff8f00] transition-transform transform hover:-translate-y-0.5 cursor-pointer relative justify-end"
          @click="Download"
        >
          Exportar
        </button>
      </div>

      <!-- Reservations Data Table -->
      <div class="table-wrapper overflow-x-auto">
        <table class="reservations-table">
          <thead>
            <tr>
              <!-- Non-sortable columns -->
              <th>
                <span class="header-cell">Id Reserva</span>
              </th>

              <!-- Sortable columns with click handlers -->
              <th
                @click="sortBy('creationDate')"
                :class="{
                  sortable: true,
                  sorted: store.filters.orderBy === 'creationDate',
                }"
              >
                <span class="header-cell">
                  Data de Criação
                  <i class="sort-icon" :class="getSortIcon('creationDate')"></i>
                </span>
              </th>

              <th
                @click="sortBy('employee')"
                :class="{
                  sortable: true,
                  sorted: store.filters.orderBy === 'employee',
                }"
              >
                <span class="header-cell">
                  Funcionário
                  <i class="sort-icon" :class="getSortIcon('employee')"></i>
                </span>
              </th>

              <th
                @click="sortBy('reservationDestination')"
                :class="{
                  sortable: true,
                  sorted: store.filters.orderBy === 'reservationDestination',
                }"
              >
                <span class="header-cell">
                  Destino
                  <i
                    class="sort-icon"
                    :class="getSortIcon('reservationDestination')"
                  ></i>
                </span>
              </th>

              <th
                @click="sortBy('noSeats')"
                :class="{
                  sortable: true,
                  sorted: store.filters.orderBy === 'noSeats',
                }"
              >
                <span class="header-cell">
                  Nº de Lugares
                  <i class="sort-icon" :class="getSortIcon('noSeats')"></i>
                </span>
              </th>

              <th
                @click="sortBy('startDate')"
                :class="{
                  sortable: true,
                  sorted: store.filters.orderBy === 'startDate',
                }"
              >
                <span class="header-cell">
                  Data de Início
                  <i class="sort-icon" :class="getSortIcon('startDate')"></i>
                </span>
              </th>

              <th
                @click="sortBy('endDate')"
                :class="{
                  sortable: true,
                  sorted: store.filters.orderBy === 'endDate',
                }"
              >
                <span class="header-cell">
                  Data de Fim
                  <i class="sort-icon" :class="getSortIcon('endDate')"></i>
                </span>
              </th>

              <th
                @click="sortBy('vehicleBrand')"
                :class="{
                  sortable: true,
                  sorted: store.filters.orderBy === 'vehicleBrand',
                }"
              >
                <span class="header-cell">
                  Veículo
                  <i class="sort-icon" :class="getSortIcon('vehicleBrand')"></i>
                </span>
              </th>

              <th
                @click="sortBy('reservationStatus')"
                :class="{
                  sortable: true,
                  sorted: store.filters.orderBy === 'reservationStatus',
                }"
              >
                <span class="header-cell">
                  Estado
                  <i
                    class="sort-icon"
                    :class="getSortIcon('reservationStatus')"
                  ></i>
                </span>
              </th>

              <th>Ações</th>
            </tr>
          </thead>

          <tbody>
            <!-- Empty State: No reservations found -->
            <tr
              v-if="
                !isLoading &&
                !isInitialLoad &&
                (!reservations || reservations.length === 0)
              "
            >
              <td colspan="10" class="empty-state-cell">
                <div class="empty-state-content">
                  <div class="text-gray-400 mb-4">
                    <svg
                      class="mx-auto h-16 w-16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linejoin="round"
                        stroke-width="1"
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  </div>
                  <h3 class="text-lg font-medium text-gray-900 mb-2">
                    Nenhuma reserva encontrada
                  </h3>
                  <p class="text-gray-500">
                    Não existem reservas que correspondam aos filtros aplicados.
                  </p>
                </div>
              </td>
            </tr>

            <!-- Loader-->
            <div
              v-if="isLoading"
              class="absolute inset-0 flex items-center justify-center z-50"
              style="background-color: rgba(255, 255, 255, 0.7)"
            >
              <div
                class="bg-white rounded-lg shadow-xl p-6 border border-gray-200 bg-opacity-95"
              >
                <div class="flex flex-col items-center space-y-3">
                  <div
                    class="animate-spin rounded-full h-10 w-10 border-b-3 border-[#28b473]"
                  ></div>
                  <div class="text-center">
                    <h3 class="text-base font-semibold text-gray-900 mb-1">
                      A procurar reservas
                    </h3>
                    <p class="text-sm text-gray-600">Por favor aguarde...</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Data Rows: Display each reservation -->
            <tr
              v-for="reservation in reservations"
              :key="reservation.reservationId"
            >
              <td>{{ reservation.reservationId }}</td>
              <td>{{ formatDate(reservation.creationDate) }}</td>
              <td>{{ reservation.employee }}</td>
              <td>{{ reservation.destination }}</td>
              <td>{{ reservation.numberOfSeats }}</td>
              <td>{{ formatDate(reservation.startDate) }}</td>
              <td>{{ formatDate(reservation.endDate) }}</td>

              <!-- Vehicle information column -->
              <td>
                <div
                  class="vehicle-info"
                  v-if="
                    reservation.vehicle &&
                    (reservation.vehicle.brand ||
                      reservation.vehicle.licensePlate)
                  "
                >
                  <span class="brand" v-if="reservation.vehicle.brand">{{
                    reservation.vehicle.brand
                  }}</span>
                  <span class="model" v-if="reservation.vehicle.model">{{
                    reservation.vehicle.model
                  }}</span>
                  <span
                    class="license-plate"
                    v-if="reservation.vehicle.licensePlate"
                    >{{ reservation.vehicle.licensePlate }}</span
                  >
                </div>
                <span v-else class="no-vehicle">-</span>
              </td>

              <!-- Status badge -->
              <td>
                <span
                  class="status-badge"
                  :class="getStatusClass(reservation.status)"
                >
                  {{
                    $t(
                      `common.scheduledReservationStatus.${reservation.status}`,
                    )
                  }}
                </span>
              </td>

              <!-- Action buttons -->
              <td>
                <div class="action-buttons">
                  <router-link
                    class="btn btn-view"
                    :to="{
                      name: 'ReservationDetails',
                      params: { id: reservation.reservationId },
                    }"
                    title="Ver detalhes"
                  >
                    <i class="fa-solid fa-magnifying-glass"></i>
                  </router-link>

                  <router-link
                    v-if="canChangeReservationReservation(reservation)"
                    class="btn btn-edit"
                    :to="{
                      name: 'ReservationEdit',
                      params: { id: reservation.reservationId },
                    }"
                    title="Editar"
                  >
                    <i class="fa-solid fa-pencil"></i>
                  </router-link>

                  <button
                    v-if="canChangeReservationReservation(reservation)"
                    class="btn btn-delete"
                    @click="cancelReservation(reservation.reservationId)"
                    title="Cancelar"
                  >
                    <i class="fa-solid fa-ban"></i>
                  </button>

                  <transition name="fade">
                    <div
                      class="popup-modal fixed inset-0 bg-opacity-50 p-10 flex items-center justify-center z-12"
                      v-if="isVisible"
                    >
                      <div
                        tabindex="0"
                        class="flex min-h-full items-end justify-center p-4 text-center focus:outline-none sm:items-center sm:p-0"
                      >
                        <div
                          class="shadow-md bg-white w-full max-w-2xl rounded-lg px-6 pt-6 pb-6 sm:p-8"
                        >
                          <!-- Title and Text Area -->
                          <div class="sm:flex sm:items-start">
                            <div
                              class="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                                data-slot="icon"
                                aria-hidden="true"
                                class="size-6 text-red-600"
                              >
                                <path
                                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </div>
                            <div
                              class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left"
                            >
                              <h3
                                id="dialog-title"
                                class="text-base font-semibold text-gray-900"
                              >
                                Cancelar Reserva
                              </h3>
                              <div class="mt-2 pb-3">
                                <!-- Body text -->
                                <p class="text-sm text-gray-500">
                                  {{ "Deseja cancelar a reserva?" }}
                                </p>
                              </div>
                            </div>
                          </div>
                          <p
                            v-if="
                              noCancelReason &&
                              (cancelReason === null || cancelReason === '')
                            "
                            class="text-red-500 text-sm mt-1"
                          >
                            Por favor, selecione um motivo.
                          </p>
                          <div
                            class="relative left-0 right-0 mt-1 bg-white rounded-lg max-h-60 flex items-center"
                          >
                            <!-- <div class="flex items-center"> -->

                            <span
                              v-if="
                                noCancelReason &&
                                (cancelReason === null || cancelReason === '')
                              "
                              class="text-red-500 text-lg font-bold mr-2"
                              >*</span
                            >

                            <select
                              v-model="cancelReason"
                              :class="[
                                'w-full p-2 pr-10 border rounded focus:outline-none appearance-none focus:ring',
                                noCancelReason &&
                                (cancelReason === null || cancelReason === '')
                                  ? 'border-red-500'
                                  : 'border-gray-300',
                              ]"
                            >
                              <option disabled value="">
                                Selecione o motivo do cancelamento
                              </option>
                              <option
                                v-for="option in cancelReasonsOptions"
                                :key="option.value"
                                :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>

                            <button
                              v-if="cancelReason"
                              @click="cancelReason = ''"
                              class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                class="w-5 h-5"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                            <div
                              v-if="!cancelReason"
                              class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>

                          <!-- Button container, placed below the text, side by side with spacing -->
                          <div
                            class="flex justify-center gap-4 mt-6 max-w-md mx-auto"
                          >
                            <!-- Cancel Button -->
                            <button
                              class="flex-1 justify-center rounded-md bg-white px-6 py-3 text-sm font-semibold text-gray-900 border-2 border-gray-300 hover:bg-gray-50"
                              @click="cancel"
                            >
                              {{ "Não" }}
                            </button>

                            <!-- Confirm Button -->
                            <button
                              class="flex-1 justify-center rounded-md bg-red-600 px-6 py-3 text-sm font-semibold text-white hover:bg-red-500"
                              @click="confirm"
                            >
                              {{ "Sim" }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Section -->
      <div class="pagination-container">
        <!-- Pagination info: showing X to Y of Z entries -->
        <div class="pagination-info">
          Mostrando {{ startEntry }} a {{ endEntry }} de
          {{ totalEntries }} entradas
        </div>

        <!-- Pagination controls -->
        <div class="pagination">
          <!-- First page button -->
          <button
            class="pagination-btn"
            :disabled="store.filters.pageNumber === 1"
            @click="goToPage(1)"
          >
            &lt;&lt;
          </button>

          <!-- Previous page button -->
          <button
            class="pagination-btn"
            :disabled="store.filters.pageNumber === 1"
            @click="goToPage(store.filters.pageNumber - 1)"
          >
            &lt;
          </button>

          <!-- Page number buttons -->
          <span class="page-numbers">
            <button
              v-for="page in visiblePages"
              :key="page"
              class="pagination-btn page-number"
              :class="{ active: page === store.filters.pageNumber }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </span>

          <!-- Next page button -->
          <button
            class="pagination-btn"
            :disabled="store.filters.pageNumber === totalPages"
            @click="goToPage(store.filters.pageNumber + 1)"
          >
            &gt;
          </button>

          <!-- Last page button -->
          <button
            class="pagination-btn"
            :disabled="store.filters.pageNumber === totalPages"
            @click="goToPage(totalPages)"
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import "@/assets/styles/datepicker-overrides.css";

// Import required components and utilities
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { useReservationStore } from "@/modules/reservations/store/reservation-store";
import { useDashboardStore } from "../store/dashboard-store";
import { pt } from "date-fns/locale";
import { useToast } from "@/core/composables/use-toast";
import { ReservationStatus } from "@/modules/reservations/models/reservation-status.enum";
import { ReservationCancelReason } from "@/modules/reservations/models/reservationCancelReason.enum";
import { ReservationDTO } from "../models/reservation";

export default {
  name: "ReservationsPage",
  components: {
    Datepicker,
  },

  mounted() {
    // if (!this.store || !this.store.reservations || this.store.reservations.length === 0)
    this.loadInitialData();
  },

  setup() {
    const { showError, showSuccess, showWarning, showInfo } = useToast();

    return {
      showError,
      showSuccess,
      showWarning,
      showInfo,
    };
  },

  data() {
    return {
      // Pagination settings
      entriesPerPageOptions: [
        { label: "10", value: 10 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],

      statusOptions: Object.values(ReservationStatus).map((enumValue) => ({
        label: this.$t(`common.scheduledReservationStatus.${enumValue}`),
        value: enumValue,
      })),

      cancelReasonsOptions: Object.values(ReservationCancelReason).map(
        (enumValue) => ({
          label: this.$t(`common.ReservationCancelReason.${enumValue}`),
          value: enumValue,
        }),
      ),

      // Date picker configuration
      pt, // Portuguese locale
      formatDateTime: "dd/MM/yyyy HH:mm",

      isOpen: false,
      cancelReason: "",
      noCancelReason: false,

      // Loading states
      isLoading: false,
      isInitialLoad: true,
      isVisible: false,
      resolve: null as any,

      // Pinia store instance
      store: useReservationStore(),
      dashboardStore: useDashboardStore(),

      // Actually applied filters (used for API calls)
      appliedFilters: { ...useReservationStore().filters },
    };
  },

  computed: {
    totalEntries() {
      return this.store?.totalItems || 0;
    },
    totalPages() {
      return this.store?.totalPages || 1;
    },
    startEntry() {
      return (
        (this.store.filters.pageNumber - 1) * this.store.filters.pageSize + 1
      );
    },
    endEntry() {
      const end = this.store.filters.pageNumber * this.store.filters.pageSize;
      return end > this.totalEntries ? this.totalEntries : end;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(
        1,
        this.store.filters.pageNumber - Math.floor(maxVisible / 2),
      );
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },
    reservations() {
      return this.store?.reservations || [];
    },
    dashboard() {
      return this.dashboardStore?.dashboard;
    },

    // Check if current filter inputs differ from applied filters
    hasUnappliedFilters() {
      return (
        this.store.filters.employee !== this.appliedFilters.employee ||
        this.store.filters.vehicleBrand !== this.appliedFilters.vehicleBrand ||
        this.store.filters.vehicleLicensePlate !==
          this.appliedFilters.vehicleLicensePlate ||
        this.store.filters.reservationId !==
          this.appliedFilters.reservationId ||
        this.store.filters.startDate !== this.appliedFilters.startDate ||
        this.store.filters.endDate !== this.appliedFilters.endDate ||
        JSON.stringify(this.store.filters.status.sort()) !==
          JSON.stringify(this.appliedFilters.status.sort())
      );
    },

    // Check if any filters are currently applied
    hasActiveFilters() {
      return (
        (this.store.filters.employee && this.store.filters.employee.trim()) ||
        (this.store.filters.vehicleBrand &&
          this.store.filters.vehicleBrand.trim()) ||
        (this.store.filters.vehicleLicensePlate &&
          this.store.filters.vehicleLicensePlate.trim()) ||
        (this.store.filters.reservationId &&
          this.store.filters.reservationId) ||
        this.store.filters.startDate ||
        this.store.filters.endDate ||
        (this.store.filters.status && this.store.filters.status.length > 0)
      );
    },
  },

  watch: {
    // Reset to first page when entries per page changes
    entriesPerPageWatch() {
      this.store.filters.pageNumber = 1;
      this.fetchReservationsFromStore();
    },
  },

  methods: {
    /**
     * Method to show the modal and initialize the promise
     */
    showModal() {
      this.isVisible = true;
      return new Promise<boolean>((resolve) => {
        this.resolve = resolve; // Save the resolve function to be called later
      });
    },

    /**
     * Method to hide the modal without performing the cancellation
     */
    cancel() {
      this.cancelReason = "";
      this.isVisible = false;
      this.noCancelReason = false;
      if (this.resolve) this.resolve(false);
      console.log("Reserva não cancelada.");
    },
    /**
     * Method to perform the confirmation (cancel reservation)
     */
    confirm() {
      if (this.cancelReason === "" || this.cancelReason === null) {
        this.noCancelReason = true;
        return;
      }
      this.isVisible = false;
      this.noCancelReason = false;
      if (this.resolve) this.resolve(true);
      console.log("Reserva cancelada.");
    },

    /**
     * Initialize the component by setting up the store and loading initial data
     */
    async loadInitialData() {
      this.isLoading = true;
      this.isInitialLoad = true;

      try {
        // Fetch initial reservations
        this.store.fetchReservations();

        this.dashboardStore.fetchDashboard();
      } catch (error) {
        console.error("Error loading initial data:", error);

        // Show error toast
        this.showError({
          message: "Não foi possível carregar as reservas. Tente novamente.",
          title: "Erro ao Carregar Dados",
        });
      } finally {
        this.isLoading = false;
        this.isInitialLoad = false;
      }
    },

    /**
     * Fetch reservations from store using current applied filters and pagination
     */
    async fetchReservationsFromStore() {
      this.isLoading = true;

      try {
        this.store.filters = {
          employee: "",
          vehicleBrand: "",
          vehicleLicensePlate: "",
          reservationId: "",
          startDate: 0,
          endDate: 0,
          status: [],
          pageNumber: this.store.filters.pageNumber,
          pageSize: this.store.filters.pageSize,
          orderBy: this.store.filters.orderBy,
          orderDirection: this.store.filters.orderDirection,
        };

        // Add applied filters to API call (not current filter inputs)
        if (
          this.appliedFilters.employee &&
          this.appliedFilters.employee.trim()
        ) {
          this.store.filters.employee = this.appliedFilters.employee.trim();
        }

        if (
          this.appliedFilters.vehicleBrand &&
          this.appliedFilters.vehicleBrand.trim()
        ) {
          this.store.filters.vehicleBrand =
            this.appliedFilters.vehicleBrand.trim();
        }

        if (
          this.appliedFilters.vehicleLicensePlate &&
          this.appliedFilters.vehicleLicensePlate.trim()
        ) {
          this.store.filters.vehicleLicensePlate =
            this.appliedFilters.vehicleLicensePlate.trim();
        }

        if (
          this.appliedFilters.reservationId &&
          this.appliedFilters.reservationId
        ) {
          this.store.filters.reservationId = this.appliedFilters.reservationId;
        }

        if (this.appliedFilters.startDate) {
          this.store.filters.startDate = new Date(
            this.appliedFilters.startDate,
          ).getTime();
        }

        if (this.appliedFilters.endDate) {
          this.store.filters.endDate = new Date(
            this.appliedFilters.endDate,
          ).getTime();
        }

        if (
          this.appliedFilters.status &&
          this.appliedFilters.status.length > 0
        ) {
          this.store.filters.status = this.appliedFilters.status;
        }
        // Call store method to fetch data
        var response = await this.store.fetchReservations();

        if (!response.isSuccess) {
          this.showError({
            message:
              response.error.message || "Erro inespeado. Tente novamente.",
          });
          return;
        }
      } catch (error: unknown) {
        console.error("Error fetching reservations:", error);

        if (error instanceof Error) {
          this.showError({
            message: error.message || "Erro inespeado. Tente novamente.",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async DownloadExcel() {
      this.isLoading = true;

      try {
        // Call store method to fetch data
        await this.store.downloadExcel();
      } catch (error) {
        console.error("Error fetching reservations:", error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Get formatted vehicle information from the nested vehicle object
     * @param {ReservationDTO} reservation - Reservation object with nested vehicle
     * @returns {string} Formatted vehicle info or empty string
     */
    getVehicleInfo(reservation: ReservationDTO) {
      if (!reservation.vehicle) return "";

      const { brand, model, licensePlate } = reservation.vehicle;
      const parts = [];

      if (brand) parts.push(brand);
      if (model) parts.push(model);
      if (licensePlate) parts.push(`(${licensePlate})`);

      return parts.join(" ");
    },

    /**
     * Navigate to a specific page
     * @param {number} page - Page number to navigate to
     */
    async goToPage(page: number) {
      if (
        page >= 1 &&
        page <= this.totalPages &&
        page !== this.store.filters.pageNumber
      ) {
        this.isLoading = true;

        try {
          this.store.filters.pageNumber = page;
          await this.fetchReservationsFromStore();
        } catch (error) {
          console.error("Error navigating to page:", error);
          // Revert page change on error
          this.store.filters.pageNumber =
            this.store.filters.pageNumber === page
              ? 1
              : this.store.filters.pageNumber;
        } finally {
          this.isLoading = false;
        }
      }
    },

    /**
     * Apply filter based on pressed dashboard card
     * @param {string} status
     */
    async cardFilter(status: string) {
      await this.clearFilters();
      this.store.filters.status = Array.of(status);
      this.appliedFilters = { ...this.store.filters };
      await this.applyFilters();
    },

    /**
     * Apply current filter inputs as active filters
     */
    async applyFilters() {
      try {
        this.isLoading = true;

        // Reset to first page when applying filters
        this.store.filters.pageNumber = 1;

        // Fetch data with new filters
        await this.fetchReservationsFromStore();
      } catch (error) {
        console.error("Error applying filters:", error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fetches excel for user download
     */
    async Download() {
      try {
        this.isLoading = true;
        // Fetch data with new filters
        await this.DownloadExcel();
      } catch (error) {
        console.error("Error downloading excel:", error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Clear all filters and reset the table
     */
    async clearFilters() {
      this.isLoading = true;

      try {
        // Reset both current and applied filters
        const emptyFilters = {
          employee: "",
          vehicleBrand: "",
          vehicleLicensePlate: "",
          reservationId: "",
          startDate: 0,
          endDate: 0,
          status: [],
          pageNumber: 1,
          pageSize: 10,
          orderBy: "creationDate",
          orderDirection: "asc",
        };

        this.store.filters = { ...emptyFilters };
        this.appliedFilters = { ...emptyFilters };

        // Fetch reservations without any filters
        await this.store.fetchReservations();
      } catch (error) {
        console.error("Error clearing filters:", error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Handle entries per page change
     */
    async onEntriesPerPageChange() {
      this.store.filters.pageNumber = 1; // Reset to first page
      await this.fetchReservationsFromStore();
    },

    /**
     * Sort table by specified column
     * @param {string} key - Column key to sort by
     */
    async sortBy(key: string) {
      this.isLoading = true;

      try {
        // Toggle sort direction or set new sort key
        if (this.store.filters.orderBy === key) {
          this.store.filters.orderDirection =
            this.store.filters.orderDirection === "asc" ? "desc" : "asc";
        } else {
          this.store.filters.orderBy = key;
          this.store.filters.orderDirection = "asc";
        }

        // Reset to first page when sorting
        this.store.filters.pageNumber = 1;

        // Fetch data with new sorting
        await this.fetchReservationsFromStore();
      } catch (error) {
        console.error("Error sorting data:", error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Get sort icon class based on current sort state
     * @param {string} column - Column name
     * @returns {string} CSS class for sort icon
     */
    getSortIcon(column: string) {
      if (this.store.filters.orderBy !== column) return "neutral";
      return this.store.filters.orderDirection === "asc" ? "asc" : "desc";
    },

    /**
     * Format date for display
     * @param {number|string|Date} dateValue - Date value to format
     * @returns {string} Formatted date string
     */
    formatDate(dateValue: number | string | Date) {
      // Return dash for empty/null/zero values
      if (!dateValue || dateValue === 0) {
        return "-";
      }

      let date;
      //TODO:validate this if
      if (typeof dateValue === "number") {
        // Handle timestamp (milliseconds)
        date = new Date(dateValue);
      } else {
        // Handle date string or Date object
        date = new Date(dateValue);
      }

      // Format to Portuguese locale
      return date.toLocaleDateString("pt-PT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    /**
     * Get CSS class for status badge
     * @param {ReservationStatus} status - Reservation status
     * @returns {string} CSS class name
     */
    getStatusClass(status: ReservationStatus) {
      const cleanStatus = status;
      const statusClasses = {
        Open: "status-open",
        Confirmed: "status-confirmed",
        Canceled: "status-canceled",
        Closed: "status-closed",
      };
      return statusClasses[cleanStatus] || "";
    },

    /**
     * Count active filters for display
     * @returns {number} Number of active filters
     */
    getActiveFiltersCount() {
      return Object.keys(this.store.filters).filter((key) => {
        if (
          ["pageNumber", "pageSize", "orderBy", "orderDirection"].includes(key)
        ) {
          return;
        }
        var value = this.store.filters[key as keyof typeof this.store.filters];

        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value && value.toString().trim();
      }).length;
    },

    /**
     * Cancel reservation with confirmation
     * @param {string} reservationId - Reservation ID
     */
    async cancelReservation(reservationId: string) {
      // Find the reservation to check its status
      const reservation = this.reservations.find(
        (r) => r.reservationId === reservationId,
      );

      // Programmatic defense - check if cancellation is allowed
      if (!reservation || !this.canChangeReservationReservation(reservation)) {
        this.showError({
          message: "Esta reserva não pode ser cancelada no estado atual.",
          title: "Ação não Permitida",
        });
        return;
      }

      const confirmed = await this.showModal();

      if (!confirmed) {
        return;
      }

      try {
        const response = await this.store.cancelReservation(
          reservationId,
          this.cancelReason as ReservationCancelReason,
        );

        if (response.isSuccess) {
          this.showSuccess({ message: "Reserva cancelada com sucesso" });
          this.cancelReason = "";
        } else {
          this.showError({
            message:
              response.error.message ||
              "Erro ao cancelar a reserva. Tente novamente.",
            title: "Erro ao Cancelar",
          });
          this.cancelReason = "";
          return;
        }

        await this.fetchReservationsFromStore();
        await this.dashboardStore.fetchDashboard();
      } catch (error) {
        console.error("Error cancelling reservation:", error);
        this.showError({
          message: "Erro ao cancelar a reserva. Tente novamente.",
          title: "Erro ao Cancelar",
        });
        this.cancelReason = "";
      }
    },

    /**
     * Check if reservation can be cancelled
     * @param {Object} reservation - Reservation object
     * @returns {boolean}
     */
    canChangeReservationReservation(reservation: ReservationDTO) {
      switch (reservation?.status) {
        case ReservationStatus.Closed:
        case ReservationStatus.Canceled: {
          return false;
        }
        case ReservationStatus.Confirmed:
        case ReservationStatus.Open: {
          return true;
        }
      }
    },
  },
};
</script>

<style scoped>
/* FILTER MENU STYLING */

.filter-menu {
  background: white;
  padding: 20px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
}

.filter-group label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

/* FILTER BUTTONS */

.filter-actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;
}

/* STATUS CHECKBOXES */

.status-checkboxes {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px 16px;
}

.status-checkboxes .checkbox-label {
  width: 50%;
  /* 2 per row */
  box-sizing: border-box;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  cursor: pointer;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.status-open .status-dot {
  background-color: #1976d2;
}

.status-confirmed .status-dot {
  background-color: #388e3c;
}

.status-canceled .status-dot {
  background-color: #d32f2f;
}

.status-closed .status-dot {
  background-color: #7b1fa2;
}

.filter-tag {
  display: inline-block;
  background-color: #dbeafe;
  color: #1e40af;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #bfdbfe;
}

.applied-filters-status {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Unapplied changes indicator */
.filter-actions button {
  position: relative;
}

/* Enhanced clear filters button when active */
.filter-actions button.bg-red-50 {
  background-color: #fef2f2 !important;
  border-color: #fecaca !important;
  color: #dc2626 !important;
}

.filter-actions button.bg-red-50:hover {
  background-color: #fee2e2 !important;
}

/* RESPONSIVE STYLES */

@media (max-width: 1200px) {
  .reservations-table {
    font-size: 12px;
  }

  .reservations-table th,
  .reservations-table td {
    padding: 8px 12px;
  }
}

@media (max-width: 768px) {
  .reservations-page {
    padding: 10px;
  }

  .table-controls {
    padding: 15px;
  }

  .filter-actions {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .filter-btn {
    flex: 1;
    min-width: 120px;
  }
}

/* PAGINATION */

.pagination-container {
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.pagination-btn {
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: white;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #4ae79e;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background-color: #28b473;
  color: white;
  border-color: #28b473;
}

.pagination .page-numbers {
  gap: 4px;
}

/* STATUS BADGES */

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  line-height: 1;
}

.status-open {
  background-color: #e3f2fd;
  color: #1976d2;
}

.status-confirmed {
  background-color: #e8f5e8;
  color: #388e3c;
}

.status-canceled {
  background-color: #ffebee;
  color: #d32f2f;
}

.status-closed {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

/* TABLE CONTAINER & STRUCTURE */

.table-wrapper {
  overflow-x: auto;
}

.table-controls {
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.entries-selector {
  display: flex;
  align-items: center;
  gap: 10px;
}

.entries-selector label {
  font-weight: 500;
  color: #555;
}

.entries-selector select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

/* TABLE STYLING */

.reservations-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
}

.reservations-table th,
.reservations-table td {
  padding: 12px 16px;
  text-align: center;
  vertical-align: middle;
  border-bottom: 1px solid #f0f0f0;
  height: 60px;
  max-height: 60px;
  overflow: hidden;
}

.reservations-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
  position: sticky;
  top: 0;
  z-index: 10;
  cursor: pointer;
  user-select: none;
  height: auto;
}

.reservations-table tbody tr {
  height: 60px;
}

.reservations-table tbody tr:hover {
  background-color: #f8f9fa;
}

/* VEHICLE INFO STYLING */

.vehicle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  height: 100%;
  align-items: center;
}

.no-vehicle {
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.brand {
  font-weight: 500;
  color: #333;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.license-plate {
  font-size: 12px;
  color: #666;
  font-family: monospace;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* ACTION BUTTONS IN TABLE */

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.btn {
  padding: 10px 10px;
  border-radius: 50px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  width: auto;
  max-width: 100px;
  height: 32px;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-view {
  background-color: #e3f2fd;
  color: #1976d2;
}

.btn-view:hover {
  background-color: #bbdefb;
}

.btn-edit {
  background-color: #fff3e0;
  color: #f57c00;
}

.btn-edit:hover {
  background-color: #ffe0b2;
}

.btn-delete {
  background-color: #ffebee;
  color: #d32f2f;
}

.btn-delete:hover {
  background-color: #ffcdd2;
}

/* SORTABLE HEADERS */

.header-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

th.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

th.sortable:hover {
  background-color: #f1f1f1;
}

.sort-icon {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  opacity: 0.3;
  transition:
    transform 0.2s,
    opacity 0.2s;
}

.sort-icon.asc {
  border-bottom: 6px solid #28b473;
  opacity: 1;
}

.sort-icon.desc {
  border-top: 6px solid #28b473;
  opacity: 1;
}

.sort-icon.neutral {
  border-top: 6px solid #535353;
}

/* Add this CSS to your table.css or general.css file */

/* Empty State Styling */
.empty-state-cell {
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}

.empty-state-content {
  padding: 48px 24px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
  margin: 8px;
  border: 2px dashed #e0e0e0;
}

.empty-state-content h3 {
  font-size: 1.125rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 8px;
}

.empty-state-content p {
  color: #6b7280;
  font-size: 0.875rem;
}

.empty-state-content svg {
  margin: 0 auto 16px auto;
  width: 64px;
  height: 64px;
  color: #d1d5db;
}

/* Loading State Styling */
.loading-state-cell {
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}

.loading-state-content {
  padding: 48px 24px;
  text-align: center;
}

.loading-state-content .flex {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* Spinner Animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .empty-state-content {
    padding: 32px 16px;
    margin: 4px;
  }

  .empty-state-content h3 {
    font-size: 1rem;
  }

  .empty-state-content svg {
    width: 48px;
    height: 48px;
  }

  /* css class for the transition */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
